/**
 * Sparkco embed mode (Task A6; token transport hardened 261007, decision G).
 *
 * Sparkco opens the app in an iframe as
 *   https://meeting.sparkco.space/?embed=1#sparkco_token=<sparkco-sso-jwt>
 * The token rides the URL FRAGMENT, which browsers never send to any server
 * (no access log, no Referer, no proxy log) — unlike the old `?token=`.
 * On load (and on every `hashchange`, which is how Sparkco pushes a re-minted
 * token without reloading the iframe) the token is copied to sessionStorage
 * and the fragment is immediately stripped with history.replaceState.
 *
 * The token is NEVER verified client-side (no secret in FE) — only its `exp`
 * claim is read to decide between "usable" and "show interstitial"; the
 * backend remains the authority (401 on any bad token).
 */
const STORAGE_KEY_FLAG = 'sparkco_embed_mode';
const STORAGE_KEY_TOKEN = 'sparkco_embed_token';
export const EMBED_TOKEN_FRAGMENT_KEY = 'sparkco_token';
/** Read `#sparkco_token=…` from a hash string ('' when absent). */
export function tokenFromHash(hash) {
    if (!hash || hash.length < 2)
        return '';
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    return params.get(EMBED_TOKEN_FRAGMENT_KEY) || '';
}
/** Copy the fragment token into sessionStorage, then strip it from the URL. */
export function captureTokenFromHash() {
    try {
        const token = tokenFromHash(window.location.hash);
        if (!token)
            return false;
        sessionStorage.setItem(STORAGE_KEY_TOKEN, token);
        // Strip the fragment — the token must not linger in history / location.
        window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
        return true;
    }
    catch (error) {
        console.error('[embedMode] Failed to capture the embed token from the URL fragment:', error);
        return false;
    }
}
/** Capture ?embed=1 + the fragment token (runs once on import). */
function captureFromUrl() {
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('embed') === '1') {
            sessionStorage.setItem(STORAGE_KEY_FLAG, '1');
        }
        if (sessionStorage.getItem(STORAGE_KEY_FLAG) === '1') {
            captureTokenFromHash();
        }
    }
    catch (error) {
        console.error('[embedMode] Failed to capture embed params from URL:', error);
    }
}
captureFromUrl();
// Re-mint path: the parent swaps only the fragment → same-document
// navigation → hashchange (no reload, no lost UI state).
try {
    window.addEventListener('hashchange', () => {
        if (isEmbedMode())
            captureTokenFromHash();
    });
}
catch {
    // non-browser environment
}
export function isEmbedMode() {
    try {
        return sessionStorage.getItem(STORAGE_KEY_FLAG) === '1';
    }
    catch {
        return false;
    }
}
export function getEmbedToken() {
    try {
        return sessionStorage.getItem(STORAGE_KEY_TOKEN);
    }
    catch {
        return null;
    }
}
/** Decode the token payload WITHOUT verification (display/expiry hints only). */
export function getEmbedClaims() {
    const token = getEmbedToken();
    if (!token)
        return null;
    const parts = token.split('.');
    if (parts.length !== 3)
        return null;
    try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));
        if (typeof payload !== 'object' || payload === null || typeof payload.user_uid !== 'string') {
            return null;
        }
        return payload;
    }
    catch {
        return null;
    }
}
/** True when a token exists and its exp claim is still in the future. */
export function isEmbedTokenUsable() {
    const claims = getEmbedClaims();
    if (!claims)
        return false;
    if (typeof claims.exp !== 'number')
        return false;
    return claims.exp * 1000 > Date.now();
}
/** Clear stored embed state (sign-out from embed context). */
export function clearEmbedState() {
    try {
        sessionStorage.removeItem(STORAGE_KEY_FLAG);
        sessionStorage.removeItem(STORAGE_KEY_TOKEN);
    }
    catch {
        // ignore
    }
}
